import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useState } from "react";

import type { Mode } from "@ranking-arena/shared/types";

import { Badge } from "./Badge";

interface TopBarProps {
  mode: Mode;
  onModeChange: (m: Mode) => void;
  user: { initials: string; email: string; displayName?: string | null };
  onSignOut: () => void | Promise<void>;
}

const MODE_ITEMS: Array<{ id: Mode; label: string }> = [
  { id: "doc-search", label: "Doc-search" },
  { id: "slide-search", label: "Slide-search" },
];

/**
 * Top bar: Arena logo + "internal · 1v1" badge on the left, mode pill
 * toggle in the middle-left, user menu on the right. Mode changes clear
 * both profile slots — the useSelection hook handles that; this component
 * just raises the event.
 *
 * The user menu is a Radix DropdownMenu: click the initials avatar to
 * reveal the signed-in email + a "Sign out" item. Post-sign-out routing
 * is handled upstream by AuthRedirect in App.tsx (it watches useAuth().user).
 */
export function TopBar({ mode, onModeChange, user, onSignOut }: TopBarProps) {
  return (
    <header
      style={{
        height: 56,
        borderBottom: "1px solid var(--border)",
        background: "var(--background)",
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        gap: 20,
        flexShrink: 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            background: "var(--atf-blue-950)",
            borderRadius: 6,
            display: "grid",
            placeItems: "center",
            color: "white",
            fontWeight: 700,
            fontSize: 14,
            fontFamily: "var(--font-mono)",
          }}
        >
          A
        </div>
        <div
          style={{
            fontFamily: "var(--font-sans)",
            fontWeight: 600,
            fontSize: 15,
            letterSpacing: "-0.005em",
          }}
        >
          Arena
        </div>
        <Badge variant="outline">internal · 1v1</Badge>
      </div>
      <div
        style={{
          marginLeft: 24,
          display: "inline-flex",
          padding: 3,
          border: "1px solid var(--border)",
          borderRadius: 8,
          background: "var(--secondary)",
        }}
      >
        {MODE_ITEMS.map((m) => {
          const active = m.id === mode;
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onModeChange(m.id)}
              style={{
                padding: "5px 12px",
                border: "none",
                cursor: "pointer",
                borderRadius: 6,
                background: active ? "var(--background)" : "transparent",
                color: active ? "var(--foreground)" : "var(--muted-foreground)",
                fontFamily: "var(--font-sans)",
                fontSize: 13,
                fontWeight: 500,
                boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                transition: "all .15s",
              }}
            >
              {m.label}
            </button>
          );
        })}
      </div>
      <div style={{ flex: 1 }} />
      <UserMenu
        initials={user.initials}
        email={user.email}
        displayName={user.displayName}
        onSignOut={onSignOut}
      />
    </header>
  );
}

function UserMenu({
  initials,
  email,
  displayName,
  onSignOut,
}: {
  initials: string;
  email: string;
  displayName?: string | null;
  onSignOut: () => void | Promise<void>;
}) {
  const [hover, setHover] = useState(false);
  const [focused, setFocused] = useState(false);

  const handleSelect = async (): Promise<void> => {
    // Let Radix close the menu on select (default behavior). Fire sign-out;
    // AuthRedirect handles the /login navigation once auth context updates.
    try {
      await onSignOut();
    } catch (err) {
      console.error("signOut failed", err);
    }
  };

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          aria-label="User menu"
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            width: 28,
            height: 28,
            borderRadius: 9999,
            background: "#fdf2f8",
            color: "#831843",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 600,
            fontSize: 28 * 0.37,
            fontFamily: "var(--font-sans)",
            flexShrink: 0,
            cursor: "pointer",
            padding: 0,
            // atf-pink-700 > --ring for contrast against the pink bg
            border: `1px solid ${hover ? "var(--atf-pink-700)" : "transparent"}`,
            boxShadow: focused ? "inset 0 0 0 2px var(--atf-pink-700)" : "none",
            transition: "border-color 120ms ease, box-shadow 120ms ease",
            outline: "none",
          }}
        >
          {initials}
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          style={{
            minWidth: 220,
            background: "var(--background)",
            border: "1px solid var(--border)",
            borderRadius: 8,
            boxShadow: "var(--shadow-dropdown)",
            padding: 4,
            zIndex: 50,
          }}
        >
          <DropdownMenu.Label
            style={{
              padding: "8px 10px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            {displayName && (
              <span
                style={{
                  fontFamily: "var(--font-sans)",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--foreground)",
                }}
              >
                {displayName}
              </span>
            )}
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 11,
                color: "var(--muted-foreground)",
                wordBreak: "break-all",
              }}
            >
              {email}
            </span>
          </DropdownMenu.Label>
          <DropdownMenu.Separator
            style={{
              height: 1,
              background: "var(--border)",
              margin: "4px 0",
            }}
          />
          <DropdownMenu.Item
            onSelect={() => {
              void handleSelect();
            }}
            style={{
              padding: "8px 10px",
              fontFamily: "var(--font-sans)",
              fontSize: 13,
              color: "var(--foreground)",
              borderRadius: 6,
              cursor: "pointer",
              outline: "none",
            }}
            className="user-menu-item"
          >
            Sign out
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
